import { type CurrencyCode } from '../../currencies/constants';
import { type SavedProduct } from '../../saves/aliases';
import { isSold } from '../helpers/isSold';
import { LegacyProductStatus } from '../types';

import { getProductPreview } from '@/modules/pictures/helpers/getProductPreview';
import { type LegacySavedProduct } from '@/modules/saves/types';

type ToLegacySavedProductInput = Pick<
  SavedProduct,
  | 'id'
  | 'slug'
  | 'videos'
  | 'sizes'
  | 'status'
  | 'pictures'
  | 'variant_set_id'
  | 'variants_all'
  | 'pricing'
  | 'is_liked'
  | 'like_count'
  | 'brand_name'
>;

function mapSizesToNames(sizes: SavedProduct['sizes']): string[] {
  if (!sizes) {
    return [];
  }

  return sizes.map(({ name }) => name ?? '').filter(Boolean);
}

export function mapPicturesToFormats(
  pictures: SavedProduct['pictures']
): Record<number, string>[] {
  if (!pictures) {
    return [];
  }

  return pictures.map((set) => {
    return Object.values(set.formats).reduce<Record<number, string>>(
      (acc, { width, url }) => {
        acc[width] = url;
        return acc;
      },
      {}
    );
  });
}

function mapVariantsToRecord(
  variants: SavedProduct['variants_all']
): Record<number, number> {
  if (!variants) {
    return {};
  }

  return variants.reduce<Record<number, number>>((acc, { id, quantity }) => {
    acc[id] = quantity;
    return acc;
  }, {});
}

function buildLegacyPricing(
  pricing: SavedProduct['pricing']
): LegacySavedProduct['pricing'] {
  return {
    ...pricing,
    currency_name: pricing.currency as CurrencyCode,
    final_price_key: pricing.final_price_key,
  };
}

function toLegacyProductStatus(
  status: SavedProduct['status']
): LegacyProductStatus {
  switch (status) {
    case 'STATUS_ONSALE':
      return LegacyProductStatus.ONSALE;
    case 'STATUS_PURCHASED':
      return LegacyProductStatus.PURCHASED;
    case 'STATUS_MARKED_AS_SOLD':
      return LegacyProductStatus.MARKED_AS_SOLD;
    case 'STATUS_DELETED':
      return LegacyProductStatus.DELETED;
    case 'STATUS_DELETED_ONSALE':
      return LegacyProductStatus.DELETED_ONSALE;
    case 'STATUS_DELETED_PURCHASED':
      return LegacyProductStatus.DELETED_PURCHASED;
    case 'STATUS_DELETED_MARKED_AS_SOLD':
      return LegacyProductStatus.DELETED_MARKED_AS_SOLD;
    case 'STATUS_PRODUCT_BANNED_ONSALE':
      return LegacyProductStatus.PRODUCT_BANNED_ONSALE;
    case 'STATUS_USER_BANNED_ONSALE':
      return LegacyProductStatus.USER_BANNED_ONSALE;
    case 'STATUS_PRODUCT_BANNED_PURCHASED':
      return LegacyProductStatus.PRODUCT_BANNED_PURCHASED;
    case 'STATUS_USER_BANNED_PURCHASED':
      return LegacyProductStatus.USER_BANNED_PURCHASED;
    default:
      // default to on sale for now
      return LegacyProductStatus.ONSALE;
  }
}

export function toLegacySavedProduct(
  product: ToLegacySavedProductInput
): LegacySavedProduct {
  return {
    id: product.id,
    slug: product.slug,
    has_video: Boolean(product.videos),
    sizes: mapSizesToNames(product.sizes),
    sold: !isSold(product.status),
    pictures: mapPicturesToFormats(product.pictures),
    preview: getProductPreview(product),
    status: toLegacyProductStatus(product.status),
    variant_set_id: product.variant_set_id ?? 0,
    variants: mapVariantsToRecord(product.variants_all),
    pricing: buildLegacyPricing(product.pricing),
    is_liked: product.is_liked,
    like_count: product.like_count,
    brand_name: product.brand_name,
  };
}
